import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import Tippy from '@tippy.js/react';
import { IconInfo } from 'svgIcons/MotionPortalIcons';
import { useTranslation } from 'react-i18next';
import './header-style.scss';
import { energyConsumptionSignals, STACKED_AREA_CHART } from '../constants';

const ViewToggleHeader = ({ currentViewMode, onViewModeChange, isOnlyEnergySignalsSelected, hasMixedSignalSelection, selectedSignalsData }) => {
    const { t: translate } = useTranslation();
    const viewModes = [
        { key: 'overall', label: 'Overall' },
        { key: 'detailed', label: 'Detailed' }
    ];
    const handleToggle = (mode) => {
        if (onViewModeChange && currentViewMode !== mode) {
            onViewModeChange(mode);
        }
    };

    // Only show when a single signal with StackedAreaChart visualization is selected
    if (!isOnlyEnergySignalsSelected || hasMixedSignalSelection) {
        return null;
    }

    // Check if any signal has StackedAreaChart visualization
    // Note: selectedSignalsData includes parent signal + sub-signals, so length will be > 1
    const hasStackedVisualization = selectedSignalsData?.some(
        item => item?.config?.details?.visualization === STACKED_AREA_CHART
    );

    if (!hasStackedVisualization) {
        return null;
    }

    // Check if signals are production or consumption based on displayName or timeseriesName
    const isProduction = selectedSignalsData?.some(item => {
        const displayName = (item?.config?.displayName || item?.config?.timeseriesName || '').toLowerCase();
        return displayName.includes(energyConsumptionSignals.PRODUCTION);
    });


    const tooltipContent = <div className='energy-view-tooltip'>
        <div id='energy-tooltip-title' className='tooltip-title'>
            <span>{isProduction ? translate('ABB.Powertrain.Frontend.DailyEnergyProduction') : translate('ABB.Powertrain.Frontend.DailyEnergyConsumption')}</span>
        </div>
        <div id='energy-tooltip-description' className='tooltip-description'>
            <span>{isProduction ? translate('ABB.Powertrain.Frontend.OverallProductionViewDescription') : translate('ABB.Powertrain.Frontend.OverallViewDescription')}</span>
        </div>
        <div id='energy-tooltip-content' className='tooltip-content'>
            <div id='energy-tooltip-measured-consumption'><strong>{isProduction ? translate('ABB.Powertrain.Frontend.MeasuredProductionLabel') : translate('ABB.Powertrain.Frontend.MeasuredConsumptionLabel')}</strong> {isProduction ? translate('ABB.Powertrain.Frontend.MeasuredProductionDescription') : translate('ABB.Powertrain.Frontend.MeasuredConsumptionDescription')}</div>
            <div id='energy-tooltip-calculated-consumption'><strong>{isProduction ? translate('ABB.Powertrain.Frontend.CalculatedProductionLabel') : translate('ABB.Powertrain.Frontend.CalculatedConsumptionLabel')}</strong> {isProduction ? translate('ABB.Powertrain.Frontend.CalculatedProductionDescription') : translate('ABB.Powertrain.Frontend.CalculatedConsumptionDescription')}</div>
        </div>
    </div>;

    return (
        <div className='view-toggle-header' id='view-toggle'>
            <div className='view-toggle-buttons'>
                {viewModes.map((mode) =>
                    <button
                        key={mode?.key}
                        type='button'
                        className={clsx('toggle-option', {
                            'active': currentViewMode === mode?.key
                        })}
                        onClick={() => handleToggle(mode?.key)}
                        aria-pressed={currentViewMode === mode?.key}
                    >
                        {mode?.label}
                    </button>
                )}
            </div>
            <div className='info-icon-container' id='info-icon'>
                <Tippy
                    content={tooltipContent}
                    interactive={true}
                    animation='scale'
                    theme='light-border'
                    trigger='mouseenter click'
                    placement='auto'
                    appendTo={() => document.body}
                    zIndex={9999}
                >
                    <button type='button' className='iconButton' id='info-icon-button' aria-label={translate('ABB.Powertrain.Frontend.info')}>
                        <IconInfo width={16} height={16} color={'black'} />
                    </button>
                </Tippy>
            </div>
        </div>
    );
};

ViewToggleHeader.propTypes = {
    currentViewMode: PropTypes.string,
    onViewModeChange: PropTypes.func,
    isOnlyEnergySignalsSelected: PropTypes.bool,
    hasMixedSignalSelection: PropTypes.bool,
    selectedSignalsData: PropTypes.array
};

export default ViewToggleHeader;
